import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AppSidebarContent } from "@/components/AppSidebarContent";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { EmailSetupDialog } from "./EmailSetupDialog";
import { EmailComposeDialog } from "./EmailComposeDialog";
import { 
  Search, 
  Edit, 
  Archive, 
  Trash, 
  Star, 
  ArrowLeft, 
  Paperclip, 
  MoreVertical,
  Reply,
  ReplyAll,
  Forward,
  RefreshCw,
  Menu,
  Inbox,
  Send,
  FileText,
  Folder,
  Mail,
  ChevronRight,
  Download,
  X,
  Settings,
  Eye,
  Loader2
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { format, isToday, isYesterday } from "date-fns";
import { de } from "date-fns/locale";

interface Email {
  id: string;
  subject: string;
  from_address: string;
  to_addresses: any;
  body_html?: string;
  body_text?: string;
  received_at: string;
  sent_at?: string;
  is_read: boolean;
  is_starred: boolean;
  attachments?: any;
  labels?: any;
  status?: string;
  message_id?: string;
  account_id?: string;
}

interface EmailAccount {
  id: string;
  email_address: string;
  display_name?: string;
  is_active: boolean;
}

interface ModernEmailClientProps {
  className?: string;
}

// Decode RFC 2047 encoded words (=?charset?encoding?text?=)
const decodeRFC2047 = (text: string): string => {
  if (!text) return '';
  
  // Join adjacent encoded words
  let processed = text.replace(/\?=\s+=\?/g, '?==?');
  
  return processed.replace(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g, (match, charset, encoding, encoded) => {
    try {
      const charsetLower = charset.toLowerCase();
      if (encoding.toUpperCase() === 'B') {
        const cleaned = encoded.replace(/[\r\n\s]/g, '');
        const bytes = Uint8Array.from(atob(cleaned), c => c.charCodeAt(0));
        try {
          return new TextDecoder(charsetLower).decode(bytes);
        } catch {
          return new TextDecoder('utf-8').decode(bytes);
        }
      } else if (encoding.toUpperCase() === 'Q') {
        let decoded = encoded.replace(/_/g, ' ');
        decoded = decoded.replace(/=([0-9A-Fa-f]{2})/g, (_, hex: string) => 
          String.fromCharCode(parseInt(hex, 16))
        );
        try {
          const bytes = Uint8Array.from(decoded, (c: string) => c.charCodeAt(0));
          return new TextDecoder(charsetLower).decode(bytes);
        } catch {
          return decoded;
        }
      }
    } catch {}
    return match;
  });
};

// Decode email subject and sender name
const decodeEmailField = (field: string | null | undefined): string => {
  if (!field) return '';
  
  let decoded = field;
  
  // First decode RFC2047 encoded words
  decoded = decodeRFC2047(decoded);
  
  // Clean up any remaining encoding artifacts
  decoded = decoded.replace(/\s+/g, ' ').trim();
  
  return decoded;
};

// Get display name from email address
const extractDisplayName = (emailString: string): { name: string; email: string } => {
  if (!emailString) return { name: 'Unbekannt', email: '' };
  
  // Decode first
  const decoded = decodeEmailField(emailString);
  
  // Format: "Name <email@domain.com>" or "email@domain.com"
  const nameEmailMatch = decoded.match(/^"?([^"<]+)"?\s*<([^>]+)>$/);
  if (nameEmailMatch) {
    return { 
      name: nameEmailMatch[1].trim(), 
      email: nameEmailMatch[2].trim() 
    };
  }
  
  // Just email
  const emailMatch = decoded.match(/<([^>]+)>/);
  if (emailMatch) {
    const email = emailMatch[1];
    return { 
      name: email.split('@')[0], 
      email 
    };
  }
  
  // Plain email
  if (decoded.includes('@')) {
    return { 
      name: decoded.split('@')[0], 
      email: decoded 
    };
  }
  
  return { name: decoded, email: decoded };
};

// Fix UTF-8 encoding issues from incorrectly decoded emails
const fixEncoding = (text: string): string => {
  if (!text) return '';
  
  // Common UTF-8 mojibake fixes (Latin-1 interpreted as UTF-8)
  const fixes: [RegExp, string][] = [
    [/Ã¤/g, 'ä'], [/Ã¶/g, 'ö'], [/Ã¼/g, 'ü'],
    [/Ã„/g, 'Ä'], [/Ã–/g, 'Ö'], [/Ãœ/g, 'Ü'],
    [/ÃŸ/g, 'ß'], [/Ã©/g, 'é'], [/Ã¨/g, 'è'],
    [/Ã /g, 'à'], [/Ã¢/g, 'â'], [/Ã®/g, 'î'],
    [/Ã´/g, 'ô'], [/Ã»/g, 'û'], [/Ã§/g, 'ç'],
    [/Ã±/g, 'ñ'], [/Ã¡/g, 'á'], [/Ã­/g, 'í'],
    [/Ã³/g, 'ó'], [/Ãº/g, 'ú'], [/â‚¬/g, '€'],
    [/â€"/g, '–'], [/â€"/g, '—'], [/â€˜/g, "'"],
    [/â€™/g, "'"], [/â€œ/g, '"'], [/â€/g, '"'],
    [/â€¦/g, '…'], [/Â /g, ' '], [/Â©/g, '©'],
    [/Â®/g, '®'], [/Â°/g, '°'], [/Â·/g, '·'],
    // Phone number special chars
    [/âª/g, ''], [/â¬/g, ''], [/\u202A/g, ''], [/\u202C/g, ''],
    // Left-to-right and right-to-left marks
    [/\u200E/g, ''], [/\u200F/g, ''],
    [/\u2028/g, '\n'], [/\u2029/g, '\n'],
  ];
  
  let fixed = text;
  for (const [pattern, replacement] of fixes) {
    fixed = fixed.replace(pattern, replacement);
  }
  
  return fixed;
};

// Clean and format HTML email content - preserve original styling
const formatEmailHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  
  // First fix encoding issues
  let cleaned = fixEncoding(html);
  
  // Remove dangerous elements but keep everything else
  cleaned = cleaned.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  cleaned = cleaned.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
  
  // Handle CID references - show a placeholder for embedded images
  cleaned = cleaned.replace(
    /<img([^>]*?)src=["']cid:([^"']+)["']([^>]*)>/gi, 
    '<span style="display:inline-block;padding:8px 12px;background:#f0f0f0;border:1px dashed #ccc;border-radius:4px;color:#666;font-size:12px;">📷 Eingebettetes Bild: $2</span>'
  );
  
  // Fix empty src attributes
  cleaned = cleaned.replace(/src=["']["']/g, 'src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"');
  
  // Don't wrap or modify the HTML structure - let it render as-is
  return cleaned;
};

// Create readable text from email body
const formatEmailText = (text: string | null | undefined): string => {
  if (!text) return '';
  
  // First fix encoding
  let cleaned = fixEncoding(text);
  
  // Remove MIME boundaries and headers
  cleaned = cleaned.replace(/--[A-Za-z0-9_=-]+(?:--)?/g, '');
  cleaned = cleaned.replace(/Content-Type:[^\n]+\n?/gi, '');
  cleaned = cleaned.replace(/Content-Transfer-Encoding:[^\n]+\n?/gi, '');
  cleaned = cleaned.replace(/Content-Disposition:[^\n]+\n?/gi, '');
  cleaned = cleaned.replace(/MIME-Version:[^\n]+\n?/gi, '');
  
  // Clean up whitespace
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
  
  return cleaned;
};

// Swipeable Email Item Component
interface SwipeableEmailItemProps {
  email: Email;
  isSelected: boolean;
  onSelect: (email: Email) => void;
  onToggleStar: (email: Email, e?: React.MouseEvent) => void;
  onSwipeDelete: (email: Email) => void;
  onMarkAsUnread: (email: Email) => void;
  getInitials: (emailAddress: string) => string;
  getSenderName: (emailAddress: string) => string;
  formatDate: (dateString: string | null | undefined) => string;
  formatEmailText: (text: string) => string;
}

const SwipeableEmailItem = ({
  email,
  isSelected,
  onSelect,
  onToggleStar,
  onSwipeDelete,
  onMarkAsUnread,
  getInitials,
  getSenderName,
  formatDate,
  formatEmailText
}: SwipeableEmailItemProps) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  const isMouseDown = useRef(false);

  const SWIPE_THRESHOLD_DELETE = -80;
  const SWIPE_THRESHOLD_UNREAD = 80;

  // Touch Events
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping) return;
    
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    
    // Allow swiping both directions
    if (diff < 0) {
      setSwipeOffset(Math.max(diff, -120));
    } else if (diff > 0) {
      setSwipeOffset(Math.min(diff, 120));
    }
  };

  const handleTouchEnd = () => {
    setIsSwiping(false);
    
    if (swipeOffset < SWIPE_THRESHOLD_DELETE) {
      setSwipeOffset(-300);
      setTimeout(() => {
        onSwipeDelete(email);
      }, 200);
    } else if (swipeOffset > SWIPE_THRESHOLD_UNREAD) {
      setSwipeOffset(0);
      onMarkAsUnread(email);
    } else {
      setSwipeOffset(0);
    }
  };

  // Mouse Events for Desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    touchStartX.current = e.clientX;
    touchCurrentX.current = e.clientX;
    isMouseDown.current = true;
    setIsSwiping(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDown.current) return;
    
    touchCurrentX.current = e.clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    
    // Allow swiping both directions
    if (diff < 0) {
      setSwipeOffset(Math.max(diff, -120));
    } else if (diff > 0) {
      setSwipeOffset(Math.min(diff, 120));
    }
  };

  const handleMouseUp = () => {
    if (!isMouseDown.current) return;
    isMouseDown.current = false;
    setIsSwiping(false);
    
    if (swipeOffset < SWIPE_THRESHOLD_DELETE) {
      setSwipeOffset(-300);
      setTimeout(() => {
        onSwipeDelete(email);
      }, 200);
    } else if (swipeOffset > SWIPE_THRESHOLD_UNREAD) {
      setSwipeOffset(0);
      onMarkAsUnread(email);
    } else {
      setSwipeOffset(0);
    }
  };

  const handleMouseLeave = () => {
    if (isMouseDown.current) {
      isMouseDown.current = false;
      setIsSwiping(false);
      setSwipeOffset(0);
    }
  };

  return (
    <div className="relative overflow-hidden">
      {/* Mark as Unread Background (swipe right) */}
      <div 
        className={cn(
          "absolute inset-y-0 left-0 flex items-center justify-start px-4 bg-blue-500 transition-opacity",
          swipeOffset > 20 ? "opacity-100" : "opacity-0"
        )}
        style={{ width: swipeOffset > 0 ? swipeOffset + 20 : 0 }}
      >
        <Mail className="h-5 w-5 text-white" />
      </div>

      {/* Delete Background (swipe left) */}
      <div 
        className={cn(
          "absolute inset-y-0 right-0 flex items-center justify-end px-4 bg-destructive transition-opacity",
          swipeOffset < -20 ? "opacity-100" : "opacity-0"
        )}
        style={{ width: swipeOffset < 0 ? Math.abs(swipeOffset) + 20 : 0 }}
      >
        <Trash className="h-5 w-5 text-destructive-foreground" />
      </div>

      {/* Email Content */}
      <div
        onClick={() => !isSwiping && swipeOffset === 0 && onSelect(email)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        className={cn(
          "p-4 cursor-pointer transition-colors bg-background select-none border-l-4",
          isSelected 
            ? "bg-primary/10 border-l-primary" 
            : "hover:bg-accent/50 border-l-transparent",
          !email.is_read && "bg-blue-50 dark:bg-blue-950/40 border-l-blue-500"
        )}
        style={{ 
          transform: `translateX(${swipeOffset}px)`,
          transition: isSwiping ? 'none' : 'transform 0.2s ease-out'
        }}
      >
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarImage src="" />
            <AvatarFallback className="text-xs bg-primary/10 text-primary">
              {getInitials(email.from_address)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-0.5">
              <p className={cn(
                "text-sm truncate flex-1",
                !email.is_read && "font-semibold"
              )}>
                {getSenderName(email.from_address)}
              </p>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-transparent"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleStar(email, e);
                  }}
                >
                  <Star className={cn(
                    "h-4 w-4",
                    email.is_starred ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/40"
                  )} />
                </Button>
              </div>
            </div>
            
            <div className="flex items-center gap-2 mb-1">
              <h3 className={cn(
                "text-sm truncate flex-1",
                !email.is_read && "font-medium"
              )}>
                {email.subject || "(Kein Betreff)"}
              </h3>
              <span className="text-xs text-muted-foreground shrink-0">
                {formatDate(email.received_at || email.sent_at)}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <p className="text-xs text-muted-foreground truncate flex-1">
                {email.body_text ? formatEmailText(email.body_text).substring(0, 80) : "Keine Vorschau"}
              </p>
              {email.attachments && Array.isArray(email.attachments) && email.attachments.length > 0 && (
                <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface Profile {
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  organization_id?: string;
  phone?: string;
  email?: string;
  address?: string;
  contact_email?: string;
}

interface Organization {
  id: string;
  name: string;
  logo_url?: string;
  color?: string;
}

export const ModernEmailClient = ({ className }: ModernEmailClientProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [emails, setEmails] = useState<Email[]>([]);
  const [folderCounts, setFolderCounts] = useState<{
    inbox: number;
    starred: number;
    sent: number;
    drafts: number;
    archive: number;
    trash: number;
  }>({ inbox: 0, starred: 0, sent: 0, drafts: 0, archive: 0, trash: 0 });
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [currentFolder, setCurrentFolder] = useState("inbox");
  const [isAppSidebarOpen, setIsAppSidebarOpen] = useState(false);
  const [isFolderSidebarOpen, setIsFolderSidebarOpen] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showComposeDialog, setShowComposeDialog] = useState(false);
  const [loadingBody, setLoadingBody] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [replyData, setReplyData] = useState<{
    to: string;
    subject: string;
    body?: string;
    originalHtml?: string;
    mode: 'reply' | 'replyAll' | 'forward';
    cc?: string;
    attachments?: any[];
  } | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<{
    open: boolean;
    loading: boolean;
    data: string | null;
    contentType: string;
    filename: string;
  }>({ open: false, loading: false, data: null, contentType: '', filename: '' });

  const folders = [
    { id: "inbox", name: "Posteingang", icon: Inbox, count: folderCounts.inbox },
    { id: "starred", name: "Markiert", icon: Star, count: folderCounts.starred },
    { id: "sent", name: "Gesendet", icon: Send, count: folderCounts.sent },
    { id: "drafts", name: "Entwürfe", icon: FileText, count: folderCounts.drafts },
    { id: "archive", name: "Archiv", icon: Archive, count: folderCounts.archive },
    { id: "trash", name: "Papierkorb", icon: Trash, count: folderCounts.trash },
  ];

  const initials = profile?.first_name && profile?.last_name
    ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
    : 'U';

  useEffect(() => {
    if (user) {
      loadProfile();
      loadAccounts();
      loadEmails();
    }
  }, [user, currentFolder]);

  // Separate effect for realtime subscription
  useEffect(() => {
    if (!user) return;

    console.log('Setting up realtime subscription for emails...');
    
    const channel = supabase
      .channel('email-updates-' + user.id)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'emails',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('New email received:', payload);
          // Add new email to the list
          const newEmail = payload.new as any;
          setEmails(prev => {
            // Check if email already exists
            if (prev.some(e => e.id === newEmail.id)) return prev;
            
            const transformedEmail = {
              ...newEmail,
              to_addresses: Array.isArray(newEmail.to_addresses) ? newEmail.to_addresses : 
                           typeof newEmail.to_addresses === 'string' ? [newEmail.to_addresses] : [],
              attachments: newEmail.attachments || [],
              labels: newEmail.labels || []
            };
            
            // Add to beginning if inbox, sorted by date
            return [transformedEmail, ...prev].sort((a, b) => 
              new Date(b.received_at || b.sent_at).getTime() - new Date(a.received_at || a.sent_at).getTime()
            );
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'emails',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('📝 Email updated via Realtime:', payload.new);
          const updatedEmail = payload.new as any;
          
          const transformedEmail = {
            ...updatedEmail,
            to_addresses: Array.isArray(updatedEmail.to_addresses) ? updatedEmail.to_addresses : 
                         typeof updatedEmail.to_addresses === 'string' ? [updatedEmail.to_addresses] : [],
            attachments: updatedEmail.attachments || [],
            labels: updatedEmail.labels || []
          };
          
          // Update emails list
          setEmails(prev => prev.map(e => 
            e.id === updatedEmail.id ? transformedEmail : e
          ));
          
          // Also update selectedEmail if it's the same email
          setSelectedEmail(prev => 
            prev?.id === updatedEmail.id ? transformedEmail : prev
          );
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
      });

    return () => {
      console.log('Cleaning up realtime subscription...');
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Note: Auto-sync is now handled globally in useBackgroundEmailSync hook (App.tsx)
  const loadProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('first_name, last_name, avatar_url, organization_id, phone, email, address, contact_email')
      .eq('user_id', user.id)
      .single();
    if (data) {
      setProfile(data);
      
      // Load organization data
      if (data.organization_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('id, name, logo_url, color')
          .eq('id', data.organization_id)
          .single();
        if (orgData) setOrganization(orgData);
      }
    }
  };

  const loadAccounts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('email_accounts')
        .select('id, email_address, display_name, is_active')
        .eq('user_id', user.id);

      if (error) throw error;

      setAccounts(data || []);
      
      // Zeige Setup-Dialog wenn keine Accounts vorhanden
      if (!data || data.length === 0) {
        setShowSetupDialog(true);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadFolderCounts = async () => {
    if (!user) return;

    try {
      // Load counts for all folders in parallel
      const [inboxResult, starredResult, sentResult, draftsResult, archiveResult, trashResult] = await Promise.all([
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('status', 'received').eq('is_read', false),
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('is_starred', true),
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('status', 'sent'),
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('status', 'draft'),
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('status', 'archived'),
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('status', 'deleted'),
      ]);

      setFolderCounts({
        inbox: inboxResult.count || 0,
        starred: starredResult.count || 0,
        sent: sentResult.count || 0,
        drafts: draftsResult.count || 0,
        archive: archiveResult.count || 0,
        trash: trashResult.count || 0,
      });
    } catch (error) {
      console.error('Error loading folder counts:', error);
    }
  };

  const loadEmails = async (showLoading = true) => {
    if (!user) return;

    try {
      // Only show loading spinner on initial load, not on background refreshes
      if (showLoading && emails.length === 0) {
        setLoading(true);
      }
      
      // Always load folder counts
      loadFolderCounts();
      
      let query = supabase
        .from('emails')
        .select('*')
        .eq('user_id', user.id);

      if (currentFolder === "starred") {
        query = query.eq('is_starred', true).order('received_at', { ascending: false, nullsFirst: false });
      } else if (currentFolder === "sent") {
        query = query.eq('status', 'sent').order('sent_at', { ascending: false, nullsFirst: false });
      } else if (currentFolder === "inbox") {
        query = query.eq('status', 'received').order('received_at', { ascending: false });
      } else if (currentFolder === "drafts") {
        query = query.eq('status', 'draft').order('created_at', { ascending: false });
      } else if (currentFolder === "archive") {
        query = query.eq('status', 'archived').order('received_at', { ascending: false });
      } else if (currentFolder === "trash") {
        query = query.eq('status', 'deleted').order('received_at', { ascending: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query.limit(100);

      if (error) throw error;

      const transformedEmails = (data || []).map(email => ({
        ...email,
        to_addresses: Array.isArray(email.to_addresses) ? email.to_addresses : 
                     typeof email.to_addresses === 'string' ? [email.to_addresses] : [],
        attachments: email.attachments || [],
        labels: email.labels || []
      }));

      setEmails(transformedEmails);
    } catch (error) {
      console.error('Error loading emails:', error);
      if (showLoading) {
        toast({
          title: "Fehler",
          description: "E-Mails konnten nicht geladen werden.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const syncEmails = async () => {
    if (!user) return;

    setRefreshing(true);
    try {
      const { data: activeAccounts } = await supabase
        .from('email_accounts')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (!activeAccounts || activeAccounts.length === 0) {
        setShowSetupDialog(true);
        return;
      }

      for (const account of activeAccounts) {
        await supabase.functions.invoke('sync-emails', {
          body: { accountId: account.id }
        });
      }

      // Reload emails without showing loading spinner (realtime will also update)
      loadEmails(false);
    } catch (error) {
      console.error('Sync error:', error);
      toast({
        title: "Synchronisierung fehlgeschlagen",
        description: "E-Mails konnten nicht synchronisiert werden.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const fetchEmailBody = async (email: Email) => {
    if (email.body_html || email.body_text) return;
    
    setLoadingBody(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-email-body', {
        body: { emailId: email.id }
      });

      if (!error && data) {
        setSelectedEmail(prev => prev ? {
          ...prev,
          body_html: data.body_html,
          body_text: data.body_text
        } : null);
        
        // Update emails list
        setEmails(prev => prev.map(e => 
          e.id === email.id ? { ...e, body_html: data.body_html, body_text: data.body_text } : e
        ));
      }
    } catch (error) {
      console.error('Error fetching email body:', error);
    } finally {
      setLoadingBody(false);
    }
  };

  const markAsRead = async (email: Email) => {
    if (email.is_read) return;

    try {
      // Update local state immediately
      setEmails(prev => 
        prev.map(e => e.id === email.id ? { ...e, is_read: true } : e)
      );

      // Get active account for IMAP sync
      const activeAccount = accounts.find(a => a.is_active) || accounts[0];
      
      // Call edge function to mark on IMAP server
      const { error } = await supabase.functions.invoke('mark-email-read-imap', {
        body: {
          emailId: email.id,
          messageId: (email as any).message_id,
          accountId: activeAccount?.id
        }
      });

      if (error) {
        console.error('Error marking as read on IMAP:', error);
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAsUnread = async (email: Email) => {
    if (!email.is_read) return;

    console.log('📬 Marking email as unread:', email.id, email.subject);

    // Optimistic update for both emails list and selectedEmail
    setEmails(prev => 
      prev.map(e => e.id === email.id ? { ...e, is_read: false } : e)
    );
    setSelectedEmail(prev => 
      prev?.id === email.id ? { ...prev, is_read: false } : prev
    );

    try {
      // Get active account for IMAP sync
      const account = accounts.find(a => a.is_active) || accounts[0];
      
      const { error } = await supabase.functions.invoke('mark-email-unread-imap', {
        body: {
          emailId: email.id,
          messageId: email.message_id || '',
          accountId: email.account_id || account?.id || ''
        }
      });

      if (error) throw error;

      toast({
        title: "Als ungelesen markiert",
        description: "Die E-Mail wurde als ungelesen markiert.",
      });
    } catch (error) {
      console.error('Error marking as unread:', error);
      // Revert optimistic update on error
      setEmails(prev => 
        prev.map(e => e.id === email.id ? { ...e, is_read: true } : e)
      );
      toast({
        title: "Fehler",
        description: "E-Mail konnte nicht als ungelesen markiert werden.",
        variant: "destructive"
      });
    }
  };

  const openAttachment = async (attachment: any, index: number) => {
    if (!selectedEmail) return;
    
    setAttachmentPreview({
      open: true,
      loading: true,
      data: null,
      contentType: attachment.contentType || '',
      filename: attachment.filename || `Anhang ${index + 1}`
    });

    try {
      const { data, error } = await supabase.functions.invoke('fetch-email-attachment', {
        body: {
          emailId: selectedEmail.id,
          attachmentIndex: index,
          filename: attachment.filename || `attachment_${index}`
        }
      });

      if (error) throw error;

      if (data?.success && data.data) {
        // Clean base64 data
        const cleanData = data.data.replace(/[\r\n\s]/g, '');
        
        setAttachmentPreview(prev => ({
          ...prev,
          loading: false,
          data: cleanData,
          contentType: data.contentType || attachment.contentType || 'application/octet-stream',
          filename: data.filename || attachment.filename
        }));
      } else {
        throw new Error('Keine Daten empfangen');
      }
    } catch (error: any) {
      console.error('Attachment fetch error:', error);
      setAttachmentPreview(prev => ({ ...prev, loading: false }));
      toast({
        title: "Fehler",
        description: "Anhang konnte nicht geladen werden.",
        variant: "destructive"
      });
    }
  };

  const downloadAttachment = () => {
    if (!attachmentPreview.data) return;
    
    try {
      // Decode base64 to binary
      const binaryString = atob(attachmentPreview.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: attachmentPreview.contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachmentPreview.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Fehler",
        description: "Download fehlgeschlagen.",
        variant: "destructive"
      });
    }
  };

  const toggleStarred = async (email: Email, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    try {
      const { error } = await supabase
        .from('emails')
        .update({ is_starred: !email.is_starred })
        .eq('id', email.id);

      if (error) throw error;

      setEmails(prev => 
        prev.map(e => e.id === email.id ? { ...e, is_starred: !e.is_starred } : e)
      );
      
      if (selectedEmail?.id === email.id) {
        setSelectedEmail(prev => prev ? { ...prev, is_starred: !prev.is_starred } : null);
      }
    } catch (error) {
      console.error('Error toggling starred:', error);
    }
  };

  // State for undo functionality
  const [deletedEmailForUndo, setDeletedEmailForUndo] = useState<Email | null>(null);
  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSwipeDelete = async (email: Email) => {
    // Store email for potential undo
    const emailToDelete = { ...email };
    setDeletedEmailForUndo(emailToDelete);

    // Clear any existing undo timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
    }

    // Optimistic UI update - remove email immediately
    setEmails(prev => prev.filter(e => e.id !== email.id));
    if (selectedEmail?.id === email.id) {
      setSelectedEmail(null);
    }

    // Show toast with undo option
    toast({
      title: "E-Mail gelöscht",
      description: "Die E-Mail wird vom Server entfernt.",
      action: (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleUndoDelete(emailToDelete)}
        >
          Rückgängig
        </Button>
      ),
    });

    // Set timeout to actually delete from server after 5 seconds
    undoTimeoutRef.current = setTimeout(async () => {
      try {
        // Get account ID for this email
        const accountId = accounts.find(a => a.is_active)?.id || accounts[0]?.id;
        
        // Delete from IMAP and database
        const { error } = await supabase.functions.invoke('delete-email-imap', {
          body: {
            emailId: email.id,
            messageId: (email as any).message_id,
            accountId
          }
        });

        if (error) {
          console.error('Error deleting email from IMAP:', error);
        }

        setDeletedEmailForUndo(null);
      } catch (err) {
        console.error('Failed to delete email:', err);
      }
    }, 5000);
  };

  const handleUndoDelete = async (email: Email) => {
    // Clear the delete timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }

    // Add email back to list
    setEmails(prev => {
      if (prev.some(e => e.id === email.id)) return prev;
      return [email, ...prev].sort((a, b) => 
        new Date(b.received_at || b.sent_at || '').getTime() - 
        new Date(a.received_at || a.sent_at || '').getTime()
      );
    });

    setDeletedEmailForUndo(null);

    toast({
      title: "Wiederhergestellt",
      description: "E-Mail wurde wiederhergestellt.",
    });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    };
  }, []);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    if (isToday(date)) {
      return format(date, 'HH:mm', { locale: de });
    } else if (isYesterday(date)) {
      return 'Gestern';
    } else {
      return format(date, 'dd.MM.yy', { locale: de });
    }
  };

  const getInitials = (emailAddress: string) => {
    const { name } = extractDisplayName(emailAddress);
    if (!name) return '??';
    const parts = name.split(/[\s._-]+/);
    return parts.map(part => part.charAt(0).toUpperCase()).join('').slice(0, 2);
  };

  const getSenderName = (emailAddress: string) => {
    return extractDisplayName(emailAddress).name;
  };

  const getSenderEmail = (emailAddress: string) => {
    return extractDisplayName(emailAddress).email;
  };

  const filteredEmails = emails.filter(email =>
    email.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    email.from_address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEmailSelect = async (email: Email) => {
    setSelectedEmail(email);
    markAsRead(email);
    setIsFolderSidebarOpen(false);
    
    // Fetch body if not present
    if (!email.body_html && !email.body_text) {
      await fetchEmailBody(email);
    }
  };

  const renderEmailContent = () => {
    if (!selectedEmail) return null;
    
    const bodyHtml = selectedEmail.body_html;
    const bodyText = selectedEmail.body_text;
    
    if (loadingBody) {
      return (
        <div className="flex items-center justify-center p-8">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">E-Mail wird geladen...</span>
        </div>
      );
    }
    
    if (bodyHtml && bodyHtml.length > 10) {
      const cleanHtml = formatEmailHtml(bodyHtml);
      
      // Create a complete HTML document for the iframe to properly render email styling
      const iframeContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              body {
                margin: 0;
                padding: 16px;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                font-size: 14px;
                line-height: 1.5;
                color: #1a1a1a;
                background: transparent;
              }
              img { max-width: 100%; height: auto; }
              a { color: #0066cc; }
              table { max-width: 100%; }
            </style>
          </head>
          <body>${cleanHtml}</body>
        </html>
      `;
      
      return (
        <iframe 
          srcDoc={iframeContent}
          className="w-full border-0 bg-white rounded-md"
          style={{ 
            minHeight: '400px',
            height: 'auto',
          }}
          sandbox="allow-same-origin"
          onLoad={(e) => {
            // Auto-resize iframe to content height
            const iframe = e.target as HTMLIFrameElement;
            try {
              const contentHeight = iframe.contentDocument?.body?.scrollHeight || 400;
              iframe.style.height = Math.max(400, contentHeight + 32) + 'px';
            } catch {}
          }}
        />
      );
    }
    
    if (bodyText && bodyText.length > 10) {
      const formatted = formatEmailText(bodyText);
      return (
        <div className="whitespace-pre-wrap text-sm font-sans leading-relaxed">
          {formatted}
        </div>
      );
    }
    
    return (
      <div className="text-muted-foreground text-center py-8">
        <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>Kein E-Mail-Inhalt verfügbar</p>
        <Button 
          variant="outline" 
          size="sm" 
          className="mt-4"
          onClick={() => fetchEmailBody(selectedEmail)}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Erneut laden
        </Button>
      </div>
    );
  };

  // Folder Sidebar Component
  const FolderSidebar = () => (
    <div className="h-full bg-card/50 flex flex-col">
      <div className="p-4 border-b">
        <Button 
          className="w-full justify-start gap-2" 
          size="sm"
        >
          <Edit className="h-4 w-4" />
          Neue E-Mail
        </Button>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {folders.map((folder) => {
            const Icon = folder.icon;
            return (
              <Button
                key={folder.id}
                variant={currentFolder === folder.id ? "secondary" : "ghost"}
                className="w-full justify-between h-10"
                onClick={() => {
                  setCurrentFolder(folder.id);
                  setIsFolderSidebarOpen(false);
                }}
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-4 w-4" />
                  <span>{folder.name}</span>
                </div>
                {folder.count > 0 && (
                  <Badge variant="secondary" className="text-xs h-5 min-w-[20px]">
                    {folder.count}
                  </Badge>
                )}
              </Button>
            );
          })}
        </div>
      </ScrollArea>
      
      <div className="p-4 border-t">
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full justify-start gap-2 text-muted-foreground"
          onClick={() => setShowSetupDialog(true)}
        >
          <Settings className="h-4 w-4" />
          E-Mail-Einstellungen
        </Button>
        
        {accounts.length > 0 && (
          <div className="mt-2 text-xs text-muted-foreground px-2">
            {accounts[0]?.email_address}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={cn("h-screen flex flex-col bg-background w-full overflow-hidden", className)}>
      {/* App Sidebar as Sheet Overlay */}
      <Sheet open={isAppSidebarOpen} onOpenChange={setIsAppSidebarOpen}>
        <SheetContent 
          side="left" 
          className="!p-0 overflow-y-auto bg-sidebar text-sidebar-foreground border-sidebar-border" 
          style={{ width: '16rem', maxWidth: '16rem' }}
          hideCloseButton
        >
          <VisuallyHidden>
            <span>Navigation</span>
          </VisuallyHidden>
          <AppSidebarContent onNavigate={() => setIsAppSidebarOpen(false)} hideActiveState />
        </SheetContent>
      </Sheet>

      {/* Email Setup Dialog */}
      <EmailSetupDialog 
        open={showSetupDialog} 
        onOpenChange={setShowSetupDialog}
        onSuccess={() => {
          loadAccounts();
          syncEmails();
        }}
      />

      {/* Header */}
      <header className="h-14 bg-primary flex items-center px-3 md:px-4 gap-2 md:gap-4 shrink-0 sticky top-0 z-50">
        {/* Left Section */}
        <div className="flex items-center gap-2 md:gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsAppSidebarOpen(true)}
            className="text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 h-9 w-9"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          {/* Mobile Folder Sidebar Toggle */}
          <Sheet open={isFolderSidebarOpen} onOpenChange={setIsFolderSidebarOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 h-9 w-9"
              >
                <Folder className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="!p-0 w-64" hideCloseButton>
              <VisuallyHidden>
                <span>E-Mail-Ordner</span>
              </VisuallyHidden>
              <FolderSidebar />
            </SheetContent>
          </Sheet>

          {/* Title */}
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary-foreground hidden md:block" />
            <h1 className="text-lg font-semibold text-primary-foreground">Mail</h1>
          </div>
        </div>

        {/* Center - Search */}
        <div className="flex-1 max-w-xl mx-auto px-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-primary-foreground/50" />
            <Input
              placeholder="E-Mails durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-primary-foreground/10 border-0 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-1 focus-visible:ring-primary-foreground/30 h-9"
            />
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-1 md:gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={syncEmails}
            disabled={refreshing}
            className="text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 h-9 w-9"
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </Button>
          
          <Avatar className="h-8 w-8 border border-primary-foreground/20">
            <AvatarImage src={profile?.avatar_url || ''} />
            <AvatarFallback className="bg-primary-foreground/10 text-primary-foreground text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Folder Sidebar - Desktop */}
        <div className="hidden lg:flex lg:flex-col w-56 border-r shrink-0 h-full overflow-hidden">
          <FolderSidebar />
        </div>

        {/* Email List */}
        <div className={cn(
          "w-full lg:w-80 xl:w-96 border-r bg-card/30 shrink-0 flex flex-col h-full overflow-hidden",
          selectedEmail ? "hidden lg:flex" : "flex"
        )}>
          <ScrollArea className="flex-1 h-full">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground mt-3">E-Mails werden geladen...</p>
              </div>
            ) : filteredEmails.length === 0 ? (
              <div className="p-8 text-center">
                <Mail className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground font-medium">Keine E-Mails</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  {accounts.length === 0 
                    ? "Richten Sie ein E-Mail-Konto ein" 
                    : "Klicken Sie auf Aktualisieren, um E-Mails zu laden"}
                </p>
                {accounts.length === 0 && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="mt-4"
                    onClick={() => setShowSetupDialog(true)}
                  >
                    E-Mail-Konto einrichten
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {filteredEmails.map((email) => (
                  <SwipeableEmailItem
                    key={email.id}
                    email={email}
                    isSelected={selectedEmail?.id === email.id}
                    onSelect={handleEmailSelect}
                    onToggleStar={toggleStarred}
                    onSwipeDelete={handleSwipeDelete}
                    onMarkAsUnread={markAsUnread}
                    getInitials={getInitials}
                    getSenderName={getSenderName}
                    formatDate={formatDate}
                    formatEmailText={formatEmailText}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Email Content */}
        <div className={cn(
          "flex-1 bg-background flex flex-col h-full overflow-hidden",
          !selectedEmail ? "hidden lg:flex" : "flex"
        )}>
          {selectedEmail ? (
            <div className="flex flex-col h-full w-full overflow-hidden">
              {/* Email Header */}
              <div className="shrink-0 border-b bg-card/50 p-4 md:p-6">
                {/* Back Button - Mobile */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedEmail(null)}
                  className="lg:hidden mb-3 -ml-2 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Zurück
                </Button>
                
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <h1 className="text-lg md:text-xl font-semibold mb-3 break-words">
                      {selectedEmail.subject || "(Kein Betreff)"}
                    </h1>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                          {getInitials(selectedEmail.from_address)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">
                          {getSenderName(selectedEmail.from_address)}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {getSenderEmail(selectedEmail.from_address)}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground ml-auto shrink-0">
                        {formatDate(selectedEmail.received_at || selectedEmail.sent_at)}
                      </span>
                    </div>
                  </div>
                  
                </div>
                
                {Array.isArray(selectedEmail.to_addresses) && selectedEmail.to_addresses.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">An:</span> {selectedEmail.to_addresses.join(', ')}
                  </div>
                )}

                <div className="flex items-center gap-1 shrink-0 mt-2 justify-end">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => {
                      const senderEmail = getSenderEmail(selectedEmail.from_address);
                      const originalSubject = decodeEmailField(selectedEmail.subject || '');
                      const replySubject = originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`;
                      const quotedBody = `\n\n\n-------- Ursprüngliche Nachricht --------\nVon: ${selectedEmail.from_address}\nDatum: ${formatDate(selectedEmail.received_at || selectedEmail.sent_at)}\nBetreff: ${originalSubject}\n\n${selectedEmail.body_text || ''}`;
                      
                      setReplyData({
                        to: senderEmail,
                        subject: replySubject,
                        body: quotedBody,
                        originalHtml: selectedEmail.body_html || undefined,
                        mode: 'reply',
                        attachments: selectedEmail.attachments || undefined
                      });
                      setShowComposeDialog(true);
                    }}
                    title="Antworten"
                  >
                    <Reply className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => {
                      const senderEmail = getSenderEmail(selectedEmail.from_address);
                      const originalSubject = decodeEmailField(selectedEmail.subject || '');
                      const replySubject = originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`;
                      const ccAddresses = Array.isArray(selectedEmail.to_addresses) 
                        ? selectedEmail.to_addresses.filter((addr: string) => addr !== accounts[0]?.email_address).join(', ')
                        : '';
                      const quotedBody = `\n\n\n-------- Ursprüngliche Nachricht --------\nVon: ${selectedEmail.from_address}\nDatum: ${formatDate(selectedEmail.received_at || selectedEmail.sent_at)}\nBetreff: ${originalSubject}\n\n${selectedEmail.body_text || ''}`;
                      
                      setReplyData({
                        to: senderEmail,
                        subject: replySubject,
                        body: quotedBody,
                        originalHtml: selectedEmail.body_html || undefined,
                        mode: 'replyAll',
                        cc: ccAddresses,
                        attachments: selectedEmail.attachments || undefined
                      });
                      setShowComposeDialog(true);
                    }}
                    title="Allen antworten"
                  >
                    <ReplyAll className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => {
                      const originalSubject = decodeEmailField(selectedEmail.subject || '');
                      const fwdSubject = originalSubject.startsWith('Fwd:') ? originalSubject : `Fwd: ${originalSubject}`;
                      const quotedBody = `\n\n\n-------- Weitergeleitete Nachricht --------\nVon: ${selectedEmail.from_address}\nDatum: ${formatDate(selectedEmail.received_at || selectedEmail.sent_at)}\nBetreff: ${originalSubject}\n\n${selectedEmail.body_text || ''}`;
                      
                      setReplyData({
                        to: '',
                        subject: fwdSubject,
                        body: quotedBody,
                        originalHtml: selectedEmail.body_html || undefined,
                        mode: 'forward',
                        attachments: selectedEmail.attachments || undefined
                      });
                      setShowComposeDialog(true);
                    }}
                    title="Weiterleiten"
                  >
                    <Forward className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={(e) => toggleStarred(selectedEmail, e)}
                    title={selectedEmail.is_starred ? "Markierung entfernen" : "Markieren"}
                  >
                    <Star className={cn(
                      "h-4 w-4",
                      selectedEmail.is_starred ? "fill-yellow-400 text-yellow-400" : ""
                    )} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={async () => {
                      if (selectedEmail) {
                        const { error } = await supabase.from('emails').delete().eq('id', selectedEmail.id);
                        if (!error) {
                          setEmails(prev => prev.filter(e => e.id !== selectedEmail.id));
                          setSelectedEmail(null);
                          toast({ title: "Gelöscht", description: "E-Mail wurde gelöscht" });
                        } else {
                          toast({ title: "Fehler", description: "E-Mail konnte nicht gelöscht werden", variant: "destructive" });
                        }
                      }
                    }}
                    title="Löschen"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>

                {/* Attachments Preview - Apple Mail Style */}
                {selectedEmail.attachments && Array.isArray(selectedEmail.attachments) && selectedEmail.attachments.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <Paperclip className="h-3 w-3" />
                      {selectedEmail.attachments.length} {selectedEmail.attachments.length === 1 ? 'Anhang' : 'Anhänge'}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {selectedEmail.attachments.map((attachment: any, index: number) => {
                        const isImage = attachment.contentType?.startsWith('image/') || 
                          /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(attachment.filename || '');
                        const isPdf = attachment.contentType?.includes('pdf') || 
                          /\.pdf$/i.test(attachment.filename || '');
                        
                        return (
                          <div
                            key={index}
                            className="group relative bg-muted/50 rounded-xl overflow-hidden border border-border/50 hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer"
                            style={{ width: isImage ? '160px' : '200px' }}
                            onClick={() => openAttachment(attachment, index)}
                          >
                            {/* Preview Area */}
                            <div className={cn(
                              "flex items-center justify-center bg-gradient-to-br",
                              isImage ? "from-blue-500/10 to-purple-500/10 h-24" : 
                              isPdf ? "from-red-500/10 to-orange-500/10 h-20" :
                              "from-gray-500/10 to-slate-500/10 h-20"
                            )}>
                              {isImage ? (
                                <div className="w-full h-full flex items-center justify-center">
                                  <FileText className="h-10 w-10 text-blue-500/60" />
                                </div>
                              ) : isPdf ? (
                                <div className="flex flex-col items-center gap-1">
                                  <FileText className="h-10 w-10 text-red-500/70" />
                                  <span className="text-[10px] font-bold text-red-500/70 uppercase">PDF</span>
                                </div>
                              ) : (
                                <FileText className="h-10 w-10 text-muted-foreground/50" />
                              )}
                            </div>
                            
                            {/* Info Area */}
                            <div className="p-3 bg-card/80 backdrop-blur-sm">
                              <p className="text-xs font-medium truncate">
                                {attachment.filename || `Anhang ${index + 1}`}
                              </p>
                              <div className="flex items-center justify-between mt-1">
                                <p className="text-[10px] text-muted-foreground">
                                  {attachment.size ? `${Math.round(attachment.size / 1024)} KB` : 'Größe unbekannt'}
                                </p>
                                <Eye className="h-3 w-3 text-muted-foreground group-hover:text-primary transition-colors" />
                              </div>
                            </div>
                            
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Email Body - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4 md:p-6">
                  {renderEmailContent()}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full w-full text-center p-8">
              <div>
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-medium mb-2">Wählen Sie eine E-Mail aus</h3>
                <p className="text-muted-foreground text-sm">
                  Klicken Sie auf eine E-Mail in der Liste, um sie zu lesen.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile FAB */}
      <div className="lg:hidden fixed bottom-6 right-6 z-40">
        <Button 
          size="lg" 
          className="h-14 w-14 rounded-full shadow-lg"
          onClick={() => { setReplyData(null); setShowComposeDialog(true); }}
        >
          <Edit className="h-6 w-6" />
        </Button>
      </div>

      {/* Compose Dialog */}
      <EmailComposeDialog
        open={showComposeDialog}
        onOpenChange={(open) => {
          setShowComposeDialog(open);
          if (!open) setReplyData(null);
        }}
        fromAddress={accounts.find(a => a.is_active)?.email_address || accounts[0]?.email_address || ""}
        replyTo={replyData}
        senderName={organization?.name}
        organization={organization || undefined}
        accountId={accounts.find(a => a.is_active)?.id || accounts[0]?.id}
        organizationId={profile?.organization_id}
        onEmailSent={() => loadEmails()}
        userProfile={profile ? {
          first_name: profile.first_name,
          last_name: profile.last_name,
          phone: profile.phone,
          email: profile.email,
          address: profile.address,
          contact_email: profile.contact_email,
          avatar_url: profile.avatar_url
        } : null}
      />

      {/* Attachment Preview Dialog */}
      <Dialog open={attachmentPreview.open} onOpenChange={(open) => setAttachmentPreview(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              {attachmentPreview.filename}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto min-h-0">
            {attachmentPreview.loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Anhang wird geladen...</span>
              </div>
            ) : attachmentPreview.data ? (
              <div className="space-y-4">
                {/* Preview based on content type */}
                {attachmentPreview.contentType.startsWith('image/') ? (
                  <div className="flex justify-center">
                    <img 
                      src={`data:${attachmentPreview.contentType};base64,${attachmentPreview.data}`}
                      alt={attachmentPreview.filename}
                      className="max-w-full max-h-[60vh] object-contain rounded-lg"
                    />
                  </div>
                ) : attachmentPreview.contentType === 'application/pdf' ? (
                  <iframe
                    src={`data:application/pdf;base64,${attachmentPreview.data}`}
                    className="w-full h-[60vh] rounded-lg border"
                    title={attachmentPreview.filename}
                  />
                ) : attachmentPreview.contentType.startsWith('text/') ? (
                  <pre className="p-4 bg-muted rounded-lg overflow-auto max-h-[60vh] text-sm">
                    {atob(attachmentPreview.data)}
                  </pre>
                ) : (
                  <div className="text-center py-10">
                    <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground mb-2">
                      Vorschau für diesen Dateityp nicht verfügbar
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {attachmentPreview.contentType}
                    </p>
                  </div>
                )}
                
                {/* Download Button */}
                <div className="flex justify-center pt-4 border-t">
                  <Button onClick={downloadAttachment} className="gap-2">
                    <Download className="h-4 w-4" />
                    Herunterladen
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-10 text-muted-foreground">
                <X className="h-12 w-12 mx-auto mb-2 opacity-50" />
                Anhang konnte nicht geladen werden
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModernEmailClient;
